import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Handler from "../../Helpers/Handler";
import { formatDateTime, textCapitalize } from "../../Helpers/Utils";
import BackButton from "../../Components/BackButton";
import Loading from "../../Components/Loading";
import Timeline from "../../Components/Timeline";
import ModelFormModal from "./ModelFormModal";
import MaintenancePanel from "../VehicleFleet/Maintenance/MaintenancePanel";
import ProfileAvatar from "../../Assets/images/avatar.png";

// Label/value row in the detail pane - matches the admin panel's existing
// col-form-label pattern used by the other View pages.
const DetailRow = ({ label, value, capitalize = false }) => (
    <div className="row align-items-sm-center mb-2">
        <label className="fw-medium col-sm-6 col-form-label">{label}</label>
        <div className={`col-sm-6 d-flex align-items-center ${capitalize ? "text-capitalize" : ""}`}>
            {value || value === 0 ? value : "-"}
        </div>
    </div>
);

const ModelDetail = () => {
    const { modelId } = useParams();
    const navigate = useNavigate();
    const [model, setModel] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [togglingStatus, setTogglingStatus] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const [modelResponse, logResponse] = await Promise.all([
            Handler({ method: "GET", url: `/admin/vehicle/model/${modelId}` }),
            Handler({ method: "GET", url: `/admin/vehicle/model/${modelId}/log`, params: { limit: 20 } }),
        ]);
        setModel(modelResponse?.success ? modelResponse?.data?.data : null);
        setLogs(logResponse?.success ? logResponse?.data?.data || [] : []);
        setLoading(false);
    }, [modelId]);

    useEffect(() => { load(); }, [load]);

    const toggleStatus = async () => {
        setTogglingStatus(true);
        const response = await Handler({ method: "patch", url: `/admin/vehicle/model/${modelId}/status`, data: { status: !model?.status } });
        if (response?.success) {
            toast.success(response?.data?.message || "Status updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update status"));
        }
        setTogglingStatus(false);
    };

    if (loading) {
        return (
            <Loading />
        );
    }

    if (!model) {
        return (
            <div className="container-fluid">
                <BackButton name="to models" url="/model" className="mb-3" />
                <div className="card border-0 shadow-sm rounded-4">
                    <div className="card-body text-center py-5">
                        <p className="text-muted mb-0">Model not found.</p>
                    </div>
                </div>
            </div>
        );
    }

    const isActive = model.status === true;
    // A model saved before the switch existed counts as on, the same rule the customer site uses.
    const siteShown = model.siteData?.enabled !== false;

    return (
        <React.Fragment>
            <div className="container-fluid">
                <BackButton name="to models" url="/model" className="mb-3" />

                <div className="row g-4">
                    <div className="col-12 col-xl-8">
                        <div className="card border-0 shadow-sm rounded-4 mb-4">
                            <div className="card-body">
                                <div className="d-flex align-items-center mb-2 justify-content-between flex-wrap gap-2">
                                    <h3 className="text-primary text-capitalize mb-0">
                                        {(model.brand?.brand || "") + " - " + (model.modelName || "")}
                                    </h3>
                                    <div className="text-end d-flex flex-wrap gap-2">
                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={toggleStatus} disabled={togglingStatus}>
                                            {togglingStatus ? "Updating..." : isActive ? "Deactivate" : "Activate"}
                                        </button>
                                        <button type="button" className="btn btn-sm btn-primary" onClick={() => setShowEditModal(true)}>Edit model</button>
                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => navigate(`/model/${modelId}/site-data`)}>Manage site data</button>
                                    </div>
                                </div>

                                <div className="d-flex align-items-center gap-3 mb-3">
                                    <img
                                        src={model.logo || ProfileAvatar}
                                        alt={model.modelName}
                                        className="rounded-circle"
                                        width={80}
                                        height={80}
                                        style={{ objectFit: "cover" }}
                                    />
                                    <span className={`badge rounded-pill ${isActive ? "text-bg-success" : "text-bg-danger"}`}>
                                        <i className={`bi ${isActive ? "bi-check-circle" : "bi-x-circle"}`}></i>
                                        {isActive ? "Active" : "Inactive"}
                                    </span>
                                    <span className={`badge rounded-pill ${siteShown ? "text-bg-success" : "text-bg-secondary"}`}>
                                        {siteShown ? "On customer site" : "Hidden from customer site"}
                                    </span>
                                </div>

                                <div className="row">
                                    <div className="col-xl-6">
                                        <DetailRow label="Brand Id" value={model.brand?.brandId} />
                                        <DetailRow label="Brand Name" value={model.brand?.brand} capitalize />
                                        <DetailRow label="Model Id" value={model.modelId} />
                                        <DetailRow label="Model Name" value={model.modelName} capitalize />
                                        <DetailRow label="Customer Site Address" value={model.seo_name ? `/models/${model.seo_name}` : "Not set yet"} />
                                    </div>
                                    <div className="col-xl-6">
                                        <DetailRow label="Segment" value={model.segment} capitalize />
                                        <DetailRow label="Sub Segment" value={model.subSegment} capitalize />
                                        <DetailRow label="Seats" value={model.seats} />
                                        <DetailRow label="Created At" value={formatDateTime(model.createdAt)} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-body">
                                <h5 className="text-capitalize text-primary mb-3">Maintenance Schedule</h5>
                                <MaintenancePanel modelId={modelId} brandType={model.brand?.brandType} />
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-xl-4">
                        <Timeline userLog={logs} />
                    </div>
                </div>
            </div>
            {showEditModal ? (
                <ModelFormModal
                    mode="edit"
                    model={model}
                    brandType={model.brand?.brandType}
                    onClose={() => setShowEditModal(false)}
                    onSaved={() => { setShowEditModal(false); load(); }}
                />
            ) : ""}

        </React.Fragment>
    );
};

export default ModelDetail;
